import { Group, Stack, Text } from "@mantine/core";

import { UploadState } from "../../../platform/media/image-upload";
import { GlassPanel } from "../../../ui/GlassPanel";
import classes from "../composer/composer.module.css";
import { PromptBox } from "../composer/PromptBox";
import { ModelChoice, OutputsChoice, RatioChoice, TypeChoice } from "../composer/Settings";
import { ComposerErrors, PriceAndStart } from "../composer/Start";
import type { ComposerProps } from "../composer/use-composer";
import { AdvancedPanel } from "./AdvancedPanel";
import { AdvancedSettings } from "./AdvancedSettings";
import { MultiShotSwitch } from "./MultiShotSwitch";
import { NegativePrompt } from "./NegativePrompt";
import { ShotsEditor } from "./ShotsEditor";
import { SoundToggle } from "./SoundToggle";
import { useVideoParts } from "./use-video-parts";
import { DurationChoice, ResolutionChoice } from "./VideoSettings";

/**
 * The video studio's floating composer (design-system.md 3.3): the frame and element slots on
 * top (live's video composer has no Advanced switch; its slots share the collapse button's
 * row), "Actions" above it, the prompt with its counter, and at the footer's left the settings
 * in live's order (model, ratio, resolution, duration, count, sound, advanced settings).
 */
export function VideoComposer(props: ComposerProps) {
  const { c, header, above, overlay, toolForm, cover, promptTags, advancedOpen, toggleAdvanced } =
    useVideoParts(props);
  const form = toolForm({ above, overlay });
  if (form !== null) return form;
  return (
    <GlassPanel
      collapsed={c.collapsed}
      onCollapsedChange={c.setCollapsed}
      header={header}
      summary={<span className={classes.summary}>{c.summary}</span>}
      above={above}
      cover={cover}
      aside={advancedOpen && c.advancedOffered ? <AdvancedPanel c={c} /> : undefined}
      overlay={overlay}
      footer={
        // Wrapping where the window is too narrow for one row (spec ui-perf/classic-create-widths).
        <Group justify="space-between" gap={12}>
          <Group gap={12}>
            <TypeChoice c={c} />
            <ModelChoice c={c} />
            <RatioChoice c={c} />
            <ResolutionChoice c={c} />
            <DurationChoice c={c} />
            <OutputsChoice c={c} />
            <SoundToggle c={c} />
            <AdvancedSettings
              c={c}
              open={advancedOpen}
              onToggle={toggleAdvanced}
            />
          </Group>
          <Group gap={12} wrap="nowrap">
            <PriceAndStart c={c} />
          </Group>
        </Group>
      }
    >
      <Stack gap={4}>
        {c.from}
        {/* Live's Multi-Shot: the shots, each with its prompt and length, replace the prompt. */}
        {c.draft.multiShot && c.multiShot !== null ? (
          <ShotsEditor c={c} />
        ) : (
          <>
            <PromptBox
              c={c}
              tags={promptTags}
            />
            <Text size="xs" c="var(--cai-color-text-muted)" ta="right">
              {c.draft.prompt.length} / {c.promptMax}
            </Text>
          </>
        )}
        <NegativePrompt c={c} />
        <UploadState upload={c.frameUpload} />
        <UploadState upload={c.endUpload} />
        <UploadState upload={c.referenceUpload} />
        <UploadState upload={c.keyframeUpload} />
        <UploadState upload={c.sourceVideoUpload} />
        <UploadState upload={c.videoUpload} />
        <UploadState upload={c.audioUpload} />
        <MultiShotSwitch c={c} />
        <ComposerErrors c={c} />
      </Stack>
      {c.dialog}
    </GlassPanel>
  );
}
